import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { AiFillPrinter } from "react-icons/ai";
import { connect } from "react-redux";
import ReactToPrint, { PrintContextConsumer } from "react-to-print";
import { toast } from "react-toastify";
import {
  getStudentList,
  getStudentDetails,
} from "../../actions/Student.action";
import { getGradeList } from "../../actions/Grade.action";
import { getWeekResult } from "../../actions/Week.action";
import { getHomeworkPerformanceResult } from "../../actions/HomeworkPerformance.action";
import { getClassworkPerformanceResult } from "../../actions/ClassworkPerformance.action";
import { getCoachingPerformanceResult } from "../../actions/CoachingPerformance.action";
import { getStudentMonthlyFees } from "../../actions/Payment.action";
import { months, years } from "../../constants/MonthsAndYears";
import { getGrade } from "../../utils/getGrade";
import { getTermLabel } from "../../utils/coachingPerformance";
import styles from "./StudentPortfolio.module.css";
import { toFortnightDisplay } from "../../utils/fortnight";

const StudentPortfolio = ({
  grades,
  getGradeList,
  getStudentList,
  getStudentDetails,
  getWeekResult,
  getHomeworkPerformanceResult,
  getClassworkPerformanceResult,
  getCoachingPerformanceResult,
  student,
  weekData,
  homeworkData,
  classworkData,
  coachingData,
}) => {
  const [grade, setGrade] = useState("");
  const [batch, setBatch] = useState("");
  const [shift, setShift] = useState("");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [uid, setUid] = useState("");
  const [loading, setLoading] = useState(false);
  const [studentInfo, setStudentInfo] = useState(null);
  const [monthlyFees, setMonthlyFees] = useState([]);

  const ref = useRef(null);

  useEffect(() => {
    if (grades === null) {
      getGradeList();
    }
  }, []);

  useEffect(() => {
    if (student) {
      setStudentInfo(student);
    }
  }, [student]);

  const selectHandeler = async () => {
    if (!year || !grade || !shift || !batch || !uid) {
      toast.error("Please select all fields!");
      return;
    }

    setLoading(true);
    setStudentInfo(null);
    setMonthlyFees([]);

    // Get student details
    const studentList = await getStudentList(year, grade, shift, batch, uid);
    if (!studentList || studentList.length !== 1) {
      toast.error("Student not found! Please check all fields.");
      setLoading(false);
      return;
    }

    const selectedStudent = studentList[0];
    await getStudentDetails(selectedStudent.id);
    setStudentInfo(selectedStudent);

    // Fetch monthly fees
    const fees = await getStudentMonthlyFees(selectedStudent.id, year);
    // Filter months based on year
    const currentMonth = new Date().getMonth(); // 0-indexed
    const currentYear = new Date().getFullYear();
    const selectedYear = parseInt(year);

    const filteredFees = fees.filter((fee, index) => {
      // For past years: show all months
      if (selectedYear < currentYear) {
        return true;
      }
      // For current year: show months up to current month, plus any paid future months
      // Don't show unpaid months after current month
      if (selectedYear === currentYear) {
        return index <= currentMonth || fee.paid;
      }
      // For future years: only show paid months
      return fee.paid;
    });
    setMonthlyFees(filteredFees);

    // Fetch all data for the selected year
    try {
      await Promise.all([
        getWeekResult(1, 52, uid, year, grade, shift, batch),
        getHomeworkPerformanceResult(
          1,
          year,
          12,
          year,
          uid,
          year,
          grade,
          shift,
          batch,
        ),
        getClassworkPerformanceResult(
          1,
          year,
          12,
          year,
          uid,
          year,
          grade,
          shift,
          batch,
        ),
        getCoachingPerformanceResult(
          1,
          year,
          3,
          year,
          uid,
          year,
          grade,
          shift,
          batch,
        ),
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    }

    setLoading(false);
  };

  // Get weekly results
  const weeklyResults = weekData?.result || [];
  // Get homework performance results
  const homeworkResults = homeworkData?.result || [];
  // Get classwork performance results
  const classworkResults = classworkData?.result || [];
  // Get coaching performance results
  const coachingResults = coachingData?.result || [];

  return (
    <Container className="pb-4">
      <Card bg="white" text="dark" className={`crd shadow mb-4`}>
        <Card.Body>
          <Row>
            <Col md={3} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="grade" className="d-block">
                  Class
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setBatch("");
                  setShift("");
                  setGrade(e.target.value);
                }}
              >
                <option value={""}>Select Class</option>
                {grades &&
                  grades.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
            {grade !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="shift" className="d-block">
                    Shift
                  </label>
                </div>
                <Form.Select
                  onChange={(e) => {
                    setBatch("");
                    setShift(e.target.value);
                  }}
                >
                  <option value={""}>Select Shift</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .shifts.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
            {grade !== "" && shift !== "" ? (
              <Col md={3} className="py-3">
                <div className="d-flex justify-content-between align-items-center pb-2">
                  <label htmlFor="batch" className="d-block">
                    Batch
                  </label>
                </div>
                <Form.Select onChange={(e) => setBatch(e.target.value)}>
                  <option value={""}>Select Batch</option>
                  {grades
                    .filter((grd) => grd.id === parseInt(grade))[0]
                    .batches.filter((bt) => bt.shiftId === parseInt(shift))
                    .map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            ) : (
              <></>
            )}
          </Row>
          <Row>
            <Col md={4} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="year" className="d-block">
                  Year
                </label>
              </div>
              <Form.Select
                onChange={(e) => {
                  setYear(e.target.value);
                }}
                id="year"
                name="year"
                value={year}
              >
                {years.map((item, i) => (
                  <option key={i} value={`${item}`}>
                    {item}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4} className="py-3">
              <div className="d-flex justify-content-between align-items-center pb-2">
                <label htmlFor="uid" className="d-block">
                  Student UID
                </label>
              </div>
              <Form.Control
                onChange={(e) => {
                  setUid(e.target.value);
                }}
                value={uid}
                type="text"
                id="uid"
                name="uid"
              />
            </Col>
            <Col
              md={4}
              className="d-flex jsutify-content-end align-items-end py-3"
            >
              <Button
                onClick={selectHandeler}
                variant="primary"
                type="submit"
                className="btn_primary"
                disabled={loading}
              >
                {loading ? "Loading..." : "Select"}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {studentInfo &&
      (weeklyResults.length > 0 ||
        homeworkResults.length > 0 ||
        classworkResults.length > 0 ||
        coachingResults.length > 0) ? (
        <div className="d-flex justify-content-end mb-3">
          <ReactToPrint
            documentTitle={`Student Portfolio - ${studentInfo.name}`}
            content={() => ref.current}
          >
            <PrintContextConsumer>
              {({ handlePrint }) => (
                <span className={styles.print_icon} onClick={handlePrint}>
                  <AiFillPrinter />
                </span>
              )}
            </PrintContextConsumer>
          </ReactToPrint>
        </div>
      ) : (
        <></>
      )}

      {loading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: 400 }}
        >
          <Spinner variant="dark" animation="grow" />
        </div>
      ) : studentInfo ? (
        <Card className={`crd ${styles.portfolio_card}`} ref={ref}>
          <Card.Body className={styles.portfolio_body}>
            <h5 className="text-center mb-4 fw-bold">
              Student Portfolio - {year}
            </h5>

            {/* Student Information */}
            <div className={styles.student_info}>
              <div className={styles.student_info_item}>
                <span className="fw-bold">Name:</span> {studentInfo.name}
              </div>
              <div className={styles.student_info_item}>
                <span className="fw-bold">UID:</span> {studentInfo.uid}
              </div>
              {student && student.grade && (
                <>
                  <div className={styles.student_info_item}>
                    <span className="fw-bold">Class:</span> {student.grade.name}
                  </div>
                  <div className={styles.student_info_item}>
                    <span className="fw-bold">Shift:</span> {student.shift.name}
                  </div>
                  <div className={styles.student_info_item}>
                    <span className="fw-bold">Batch:</span> {student.batch.name}
                  </div>
                </>
              )}
              <div className={styles.student_info_item}>
                <span className="fw-bold">Phone:</span> {studentInfo.phone}
              </div>
            </div>

            {/* Fortnight Results */}
            {weeklyResults.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Fortnight Results</h6>
                <div className={styles.fortnight_container}>
                  {weeklyResults
                    .sort((a, b) => {
                      if (a.year !== b.year) return a.year - b.year;
                      return a.week - b.week;
                    })
                    .map((item) => (
                      <Table
                        key={item.id}
                        bordered
                        size="sm"
                        className={styles.fortnight_table}
                      >
                        <thead>
                          <tr>
                            <th colSpan={2} className="text-center">
                              Fortnight {toFortnightDisplay(item.week)}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-center">
                              {item.percent.toFixed(0)}%
                            </td>
                            <td className="text-center">
                              {getGrade(item.percent)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    ))}
                </div>
              </div>
            ) : (
              <></>
            )}

            {/* Homework Performance */}
            {homeworkResults.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Homework Performance</h6>
                <div className={styles.fortnight_container}>
                  {homeworkResults
                    .sort((a, b) => {
                      if (a.year !== b.year) return a.year - b.year;
                      return a.month - b.month;
                    })
                    .map((item) => (
                      <Table
                        key={item.id}
                        bordered
                        size="sm"
                        className={styles.fortnight_table}
                      >
                        <thead>
                          <tr>
                            <th colSpan={2} className="text-center">
                              {months[item.month - 1]}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-center">
                              {item.percent.toFixed(0)}%
                            </td>
                            <td className="text-center">
                              {getGrade(item.percent)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    ))}
                </div>
              </div>
            ) : (
              <></>
            )}

            {/* Classwork Performance */}
            {classworkResults.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Classwork Performance</h6>
                <div className={styles.fortnight_container}>
                  {classworkResults
                    .sort((a, b) => {
                      if (a.year !== b.year) return a.year - b.year;
                      return a.month - b.month;
                    })
                    .map((item) => (
                      <Table
                        key={item.id}
                        bordered
                        size="sm"
                        className={styles.fortnight_table}
                      >
                        <thead>
                          <tr>
                            <th colSpan={2} className="text-center">
                              {months[item.month - 1]}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-center">
                              {item.percent.toFixed(0)}%
                            </td>
                            <td className="text-center">
                              {getGrade(item.percent)}
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    ))}
                </div>
              </div>
            ) : (
              <></>
            )}

            {/* Coaching Performance */}
            {coachingResults.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Coaching Performance</h6>
                <div className={styles.fortnight_container}>
                  {coachingResults
                    .sort((a, b) => {
                      if (a.year !== b.year) return a.year - b.year;
                      return a.term - b.term;
                    })
                    .map((item) => {
                      const termCount = studentInfo.term_count || 2;
                      return (
                        <Table
                          key={item.id}
                          bordered
                          size="sm"
                          className={styles.fortnight_table}
                        >
                          <thead>
                            <tr>
                              <th colSpan={2} className="text-center">
                                {getTermLabel(termCount, item.term)}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="text-center">
                                {item.percent.toFixed(0)}%
                              </td>
                              <td className="text-center">
                                {getGrade(item.percent)}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      );
                    })}
                </div>
              </div>
            ) : (
              <></>
            )}

            {/* Monthly Fees */}
            {monthlyFees.length > 0 ? (
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Monthly Fees</h6>
                <div className={styles.fortnight_container}>
                  {monthlyFees.map((fee) => (
                    <Table
                      key={fee.month}
                      bordered
                      size="sm"
                      className={styles.monthly_fee_table}
                    >
                      <tbody>
                        <tr className="bg-light">
                          <td className="text-center fw-bold">{fee.month}</td>
                          <td
                            className={`text-center fw-bold ${
                              fee.paid ? styles.paid : styles.unpaid
                            }`}
                          >
                            {fee.paid ? "Paid" : "Unpaid"}
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  ))}
                </div>
              </div>
            ) : (
              <></>
            )}

            {weeklyResults.length === 0 &&
            homeworkResults.length === 0 &&
            classworkResults.length === 0 &&
            coachingResults.length === 0 ? (
              <span className="text-center fs-5 d-block pb-3">
                No performance data found for this student
              </span>
            ) : (
              <></>
            )}
          </Card.Body>
        </Card>
      ) : (
        <></>
      )}
    </Container>
  );
};

const mapStateToProps = (state) => ({
  grades: state.grade.grade,
  student: state.student.selected_student,
  weekData: state.week.week,
  homeworkData: state.homeworkPerformance.homeworkPerformance,
  classworkData: state.classworkPerformance.classworkPerformance,
  coachingData: state.coachingPerformance.coachingPerformance,
});

export default connect(mapStateToProps, {
  getGradeList,
  getStudentList,
  getStudentDetails,
  getWeekResult,
  getHomeworkPerformanceResult,
  getClassworkPerformanceResult,
  getCoachingPerformanceResult,
})(StudentPortfolio);
